import { NgClass, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  Input,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { delay, startWith } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { IconKey } from '../../../../../../whitelabel/icon-key.enum';
import { AuthService } from '../../../../../core/services/auth.service';
import { LanguageService } from '../../../../../core/services/language.service';
import { ButtonComponent } from '../../../../../osp-ui/components/button/button/button.component';
import { DropdownComponent } from '../../../../../osp-ui/components/form/dropdown/dropdown.component';
import { ErrorComponent } from '../../../../../osp-ui/components/form/form-field/components/error/error.component';
import { LabelComponent } from '../../../../../osp-ui/components/form/form-field/components/label/label.component';
import { FormFieldComponent } from '../../../../../osp-ui/components/form/form-field/form-field.component';
import { ToggleButtonComponent } from '../../../../../osp-ui/components/form/toggle-button/toggle-button.component';
import { CreateGeofenceSidebarComponent } from '../../../../../shared/components/maps/components/create-geofence-sidebar/create-geofence-sidebar.component';
import { MapComponent } from '../../../../../shared/components/maps/components/map/map.component';
import { GenericCreateGeofenceCommand } from '../../../../../shared/components/maps/contract/generic-create-geofence-command.contract';
import { TheftProtectionGeofence } from '../../../../../shared/contract/theft-protection-geofence.interface';
import { Authorities } from '../../../../../shared/enums/authorities.enum';
import { GeofenceAlarmTrigger } from '../../../../../shared/enums/geofence-alarm-trigger.enum';
import { GeofenceService } from '../../../../../shared/services/geofence.service';
import { GlobalGeofencesDataSource } from '../../../../../shared/services/global-geofence-datasource.service';
import { IconProviderService } from '../../../../../whitelabel/services/icon-provider.service';
import { AlarmSettingsFieldData } from '../../../contract/alarm-settings-field-data.contract';
import { AlarmSettingsBase } from '../alarm-settings-base.class';
import { AlarmSettingsFormControls } from '../contract/alarm-settings-form-controls.contract';

interface GenericGeofence {
  value: string;
  label?: string;
}

interface GroupedGenericGeofences {
  label: string;
  value: string;
  items: GenericGeofence[];
}

@Component({
  selector: 'osp-theft-protection-geofence-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DropdownComponent,
    MapComponent,
    TranslateModule,
    FormFieldComponent,
    LabelComponent,
    ErrorComponent,
    NgClass,
    CreateGeofenceSidebarComponent,
    FormsModule,
    ButtonComponent,
    NgIf,
  ],
  templateUrl: './theft-protection-geofence-settings.component.html',
  styleUrl: './theft-protection-geofence-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TheftProtectionGeofenceSettingsComponent extends AlarmSettingsBase implements OnInit, AfterViewInit {
  @ViewChild(CreateGeofenceSidebarComponent) geofenceSidebar: CreateGeofenceSidebarComponent | undefined;
  @ViewChild(ToggleButtonComponent) toggleButton: ToggleButtonComponent | undefined;
  private readonly destroyRef = inject(DestroyRef);
  private readonly languageService = inject(LanguageService);
  private readonly geofenceService = inject(GeofenceService);
  private readonly authService = inject(AuthService);
  private readonly globalGeofencesDataSource = inject(GlobalGeofencesDataSource);
  private readonly iconProviderService = inject(IconProviderService);
  protected readonly IconKey = IconKey;

  @Input() set form(form: FormGroup<AlarmSettingsFormControls>) {
    this.settingsForm = form;
  }

  @Input() set fieldData(fieldData: AlarmSettingsFieldData) {
    this.alarmSettingsFieldData = fieldData;
    this.setLabels();
  }

  geoColor = input('red');
  protected $geofences = signal<TheftProtectionGeofence[]>([]);
  protected $groupedGeofences = signal<GroupedGenericGeofences[]>([]);
  protected $canCreateGeofence = signal(false);
  protected onUpdateGeofences = output<TheftProtectionGeofence[]>();

  protected initialCenter = signal<google.maps.LatLngLiteral | undefined>({
    lat: 50.98609893339354,
    lng: 10.290756225585938,
  });
  protected geoLabel = '';
  protected geoTriggerLabel = '';
  protected geoTriggerExplanation: string = '';
  protected triggerOptions = [
    {
      value: GeofenceAlarmTrigger.ENTERING,
      label: this.languageService.getInstant('modules.notification.alarmRuleAddEdit.triggerEnter'),
    },
    {
      value: GeofenceAlarmTrigger.LEAVING,
      label: this.languageService.getInstant('modules.notification.alarmRuleAddEdit.triggerLeave'),
    },
  ];

  private displayedPolygon: google.maps.Polygon | undefined;
  private readonly globalGeofencesMap = computed(
    () => new Map<string, TheftProtectionGeofence>(this.$geofences().map((geo) => [geo.id, geo])),
  );
  private readonly isViewInitialized = signal(false);
  private readonly isMapInitialized = signal(false);
  protected $buttonState = signal(false);
  protected map = signal<google.maps.Map | undefined>(undefined);
  protected allowedToCreateGeofence = computed(() => this.authService.hasAuthority(Authorities.GEOFENCE_MANAGE));

  constructor() {
    super();
    effect(() => {
      if (this.isViewInitialized() && this.isMapInitialized()) {
        this.initListeners();
      }
    });
  }

  ngOnInit(): void {
    this.updateGeofences();
  }

  ngAfterViewInit(): void {
    this.isViewInitialized.set(true);
  }

  public mapReady(map: google.maps.Map): void {
    this.map.set(map);
    this.isMapInitialized.set(true);
  }

  private initListeners(): void {
    if (!this.theftProtectionGeofenceControl) {
      return;
    }
    this.theftProtectionGeofenceControl.valueChanges
      .pipe(startWith(this.theftProtectionGeofenceControl.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((geofenceId: string) => this.valueChangesGeofence(geofenceId));
  }

  private setLabels(): void {
    this.geoLabel = this.getFieldLabel(this.controlNames.SETTINGS_GEOFENCE);
    this.geoTriggerLabel = this.getFieldLabel(this.controlNames.SETTINGS_GEOFENCE_TRIGGER);
    this.geoTriggerExplanation = this.getFieldTooltipText(this.controlNames.SETTINGS_GEOFENCE_TRIGGER);
  }

  private valueChangesGeofence(geofenceId: string): void {
    if (this.displayedPolygon) {
      this.displayedPolygon.setMap(null);
    }
    if (geofenceId) {
      const geo = this.globalGeofencesMap().get(geofenceId);
      this.showSingleGeofence(geo);
    }
  }

  private checkForEditMode(): void {
    const geofenceId = this.settingsForm?.get(this.controlNames.SETTINGS_GEOFENCE)?.value;
    if (geofenceId) {
      this.valueChangesGeofence(geofenceId);
    }
    const currentGeofence = this.$geofences().find((geofence) => geofence.id === geofenceId);
    if (!currentGeofence) {
      this.settingsForm?.get(this.controlNames.SETTINGS_GEOFENCE)?.reset();
    }
  }

  private showSingleGeofence(viewGeofence: TheftProtectionGeofence | undefined): void {
    if (!viewGeofence) {
      return;
    }
    const polygon = this.mapPolygonMapper(viewGeofence);
    const bounds = new google.maps.LatLngBounds();

    polygon.setVisible(true);
    polygon.setOptions({
      fillColor: viewGeofence.color,
      strokeColor: viewGeofence.color,
    });
    polygon.getPath().forEach((coord: google.maps.LatLng) => bounds.extend(coord));
    if (this.map()) {
      this.map()?.panToBounds(bounds);
      this.map()?.fitBounds(bounds);
    }

    this.displayedPolygon = polygon;
  }

  private mapPolygonMapper(geofence: TheftProtectionGeofence): google.maps.Polygon {
    return new google.maps.Polygon({
      paths: geofence.coordinates.map(({ lat, lon }) => ({ lat, lng: lon })),
      strokeColor: this.geoColor(),
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: this.geoColor(),
      fillOpacity: 0.5,
      map: this.map(),
      clickable: false,
    });
  }

  protected toggleCreateMode(): void {
    if (!this.$buttonState()) {
      this.geofenceSidebar?.handleCreateModeEnterAction();
      this.$buttonState.set(true);
    } else {
      this.geofenceSidebar?.handleCancelAction();
      this.$buttonState.set(false);
    }
  }

  handleCreateGeofenceAction(command: GenericCreateGeofenceCommand) {
    const cmd = {
      globalGeofenceName: command.geofenceName,
      globalGeofenceColor: command.geofenceColor,
      coordinates: command.coordinates,
    };
    this.globalGeofencesDataSource
      .createGlobalGeofenceObservable(cmd)
      .pipe(delay(environment.DELAY_SHORT), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (geofenceId) => {
          this.updateGeofences(geofenceId);
          this.geofenceSidebar?.handleCancelAction();
          this.$buttonState.set(false);
        },
      });
  }

  private updateGeofences(geofenceId?: string): void {
    this.geofenceService
      .loadAllGeofences()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.$geofences.set(Object.values(data).flat());

        const groupedGeofences: GroupedGenericGeofences[] = Array.from(
          Object.entries(data),
          ([groupKey, geofences]) => ({
            label: this.languageService.getInstant(
              `modules.notification.alarmRuleAddEdit.geofenceType.${groupKey.toLowerCase()}`,
            ),
            value: groupKey,
            items: geofences.map((g) => ({
              value: g.id,
              label: g.name,
            })),
          }),
        );
        this.$groupedGeofences.set(groupedGeofences);

        this.checkForEditMode();
        this.$canCreateGeofence.set(this.authService.hasAuthority(Authorities.GEOFENCE_MANAGE));

        if (geofenceId) {
          this.theftProtectionGeofenceControl?.setValue(geofenceId);
        }
        this.onUpdateGeofences.emit(data['GLOBAL']);
      });
  }

  resolveAddGeofenceButtonIcon() {
    return !this.$buttonState()
      ? this.iconProviderService.getIcon(IconKey.MapAddGeofence)
      : this.iconProviderService.getIcon(IconKey.GlobalCancel);
  }
}
